import type { ColumnMapping, VoterFieldKey } from "@direct-campaign/types";

export interface VoterFieldDef {
  key: VoterFieldKey;
  label: string;
  required: boolean;
  /** Shown in the mapper UI under the field name. */
  hint: string;
  /**
   * Normalized header names (lowercase, alphanumerics only) that auto-match
   * this field. Covers VAN exports plus common county/state file headers.
   */
  aliases: string[];
}

export const VOTER_FIELDS: VoterFieldDef[] = [
  {
    key: "voterCode",
    label: "Voter ID",
    required: false,
    hint: "Direct Campaign voter ID (DC-XXXXXXXX) from a previous export. New voters get one automatically.",
    aliases: ["voterid", "votercode", "dcid", "dcvoterid", "directcampaignid", "directcampaignvoterid"],
  },
  {
    key: "firstName",
    label: "First name",
    required: true,
    hint: "Voter's legal first name.",
    aliases: ["firstname", "first", "fname", "givenname", "voterfirstname"],
  },
  {
    key: "lastName",
    label: "Last name",
    required: true,
    hint: "Voter's legal last name.",
    aliases: ["lastname", "last", "lname", "surname", "familyname", "voterlastname"],
  },
  {
    key: "address",
    label: "Street address",
    required: true,
    hint: "Residential street address (number + street).",
    aliases: [
      "address",
      "streetaddress",
      "addressline",
      "addressline1",
      "address1",
      "regaddress",
      "residentialaddress",
      "homeaddress",
      "voterfileaddress",
      "streetname",
    ],
  },
  {
    key: "city",
    label: "City",
    required: true,
    hint: "Residential city.",
    aliases: ["city", "regcity", "residentialcity", "town", "municipality"],
  },
  {
    key: "state",
    label: "State",
    required: true,
    hint: "Two-letter state code. Full state names are converted automatically.",
    aliases: ["state", "st", "regstate", "residentialstate"],
  },
  {
    key: "zip",
    label: "ZIP code",
    required: true,
    hint: "5-digit ZIP. ZIP+4 values are trimmed automatically.",
    aliases: ["zip", "zipcode", "zip5", "zip4", "postalcode", "regzip", "residentialzip"],
  },
  {
    key: "phone",
    label: "Phone",
    required: false,
    hint: "Any phone number. Formatting characters are stripped.",
    aliases: [
      "phone",
      "phonenumber",
      "cellphone",
      "cell",
      "mobile",
      "mobilephone",
      "homephone",
      "landline",
      "telephone",
      "preferredphone",
    ],
  },
  {
    key: "email",
    label: "Email",
    required: false,
    hint: "Voter's email address.",
    aliases: ["email", "emailaddress", "voteremail"],
  },
  {
    key: "dob",
    label: "Date of birth",
    required: false,
    hint: "Accepts YYYY-MM-DD or MM/DD/YYYY. Used for deduplication when no Voter ID is present.",
    aliases: ["dob", "dateofbirth", "birthdate", "birthday", "dobdate"],
  },
  {
    key: "party",
    label: "Party",
    required: false,
    hint: "Party affiliation as it appears in the file (not normalized).",
    aliases: ["party", "partyaffiliation", "partycode", "politicalparty", "regparty"],
  },
  {
    key: "precinct",
    label: "Precinct",
    required: false,
    hint: "Voting precinct name or number.",
    aliases: ["precinct", "precinctname", "precinctcode", "precinctnumber", "precinctid"],
  },
];

export const REQUIRED_VOTER_FIELDS: VoterFieldKey[] = VOTER_FIELDS.filter((f) => f.required).map(
  (f) => f.key,
);

/** Lowercase a header and strip everything but letters and digits, so "First_Name " matches "firstname". */
export function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Suggest an initial column mapping from CSV headers. Each voter field is
 * assigned at most once — the first header that matches wins. Unmatched
 * headers map to null ("don't import").
 */
export function suggestMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const taken = new Set<VoterFieldKey>();

  for (const header of headers) {
    const normalized = normalizeHeader(header);
    const match = VOTER_FIELDS.find(
      (f) => !taken.has(f.key) && (f.aliases.includes(normalized) || normalizeHeader(f.label) === normalized),
    );
    if (match) {
      mapping[header] = match.key;
      taken.add(match.key);
    } else {
      mapping[header] = null;
    }
  }

  return mapping;
}

/**
 * Validate a column mapping before import. Returns human-readable problems;
 * an empty array means the mapping is usable.
 */
export function validateMapping(mapping: ColumnMapping): string[] {
  const problems: string[] = [];
  const counts = new Map<VoterFieldKey, string[]>();

  for (const [header, field] of Object.entries(mapping)) {
    if (field === null) continue;
    const headers = counts.get(field) ?? [];
    headers.push(header);
    counts.set(field, headers);
  }

  for (const [field, headers] of counts) {
    if (headers.length > 1) {
      const label = VOTER_FIELDS.find((f) => f.key === field)?.label ?? field;
      problems.push(`${label} is mapped to multiple columns: ${headers.join(", ")}.`);
    }
  }

  for (const field of REQUIRED_VOTER_FIELDS) {
    if (!counts.has(field)) {
      const label = VOTER_FIELDS.find((f) => f.key === field)?.label ?? field;
      problems.push(`${label} is required but no column is mapped to it.`);
    }
  }

  return problems;
}
